import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
	Component,
	computed,
	inject,
	input,
	Input,
	OnChanges,
	OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatterFileType } from '@models/matter';
import {
	StatementOfAccountDefault,
	StatementOfAccountDefaultTypes,
	StatementOfAccountType,
} from '@models/statementOfAccount';
import { BusinessStatementOfAccountsDefaultRepoService } from '@repos/business-statement-of-account-defaults/businessStatementOfAccountsDefaultRepo';
import { cloneDeep, sortBy } from 'lodash';
import { ICON } from '@core/util/icon';
import {
	AddStatementOfAccountDefaultComponent,
	AddStatementOfAccountDefaultData,
} from '../add/add.component';
import {
	EditDocumentModalData,
	EditDocumentTextComponent,
} from '../edit-document-text/edit-document-text.component';
import { soaDefaultsTitles } from '../soa-defaults.model';
import {
	SolunoActivityCode,
	SolunoActivityCodeTypes,
	SolunoGLAccount,
} from '@models/soluno';
import { GhostPracticeCode } from '@models/ghost-practice';
import { BusinessState } from '@state/business';
import { getSolunoActivityCodeLabelDescription } from '@core/util/accounting/soluno';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SearchableDropdownItems } from 'src/app/shared/atomic/atoms/searchable-dropdown/searchable-dropdown.types';
import { CostRecoveryTaskCode, FeeTaskCode } from '@models/leap';
import { LeapForBusiness } from '@models/businesses';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { getLeapCodeLabelDescription } from '@core/util/accounting/leap';
import { BusinessStatamentOfAccountSelectors } from '@state/business/statementOfAccountDefaults';
import { toSignal } from '@angular/core/rxjs-interop';
import { SoftwareTransactionFeeDisplayLocation } from '@models/businessSettings/settings';
import { GlobalStatementOfAccountSelectors } from '@state/global/statementOfAccountDefaults/statementOfAccountDefaults.selectors';
import { NotApplicableValue } from 'src/app/private/sale/accounting/accounting.models';
import { getGhostPracticeCodeLabelDescription } from 'src/app/shared/core/util/accounting/ghost-practice';
import { AccountingCodeLabels } from '../accounting-code-labels.constants';

interface StatementOfAccountDefaultExtended extends StatementOfAccountDefault {
	intercomId?: string;
}

@UntilDestroy()
@Component({
	selector: 'app-statement-of-account-defaults-list',
	templateUrl: './list.component.html',
	styleUrls: ['./list.component.scss'],
	standalone: false,
})
export class StatementOfAccountDefaultListComponent
	implements OnChanges, OnInit
{
	remainingGlobalStatements = inject(Store).selectSignal<
		StatementOfAccountDefault[]
	>(BusinessStatamentOfAccountSelectors.getRemainingGlobalDefaults);

	globalStatements = inject(Store).selectSignal<StatementOfAccountDefault[]>(
		GlobalStatementOfAccountSelectors.getAll,
	);
	public filteredOrderedStatements: StatementOfAccountDefaultExtended[] = [];
	public ICON = ICON;
	public types = StatementOfAccountType;
	public soaDefaultsTitles = soaDefaultsTitles;
	public deletedStatements = [];
	public showDeleted = false;

	@Input() title: string;
	@Input() statements: StatementOfAccountDefault[] = [];
	@Input() fileType: MatterFileType;
	@Input() statementType: StatementOfAccountType;
	@Input() showSolunoGLAccountDetails = false;

	showSolunoActivityCodeDropdown = input<boolean>(false);
	showLeapFeeTaskCodesDropdown = input<boolean>(true);
	showGhostPracticeCodeDropdown = input<boolean>(false);

	@Input('solunoActivityCodes') set _solunoActivityCodes(
		codes: SolunoActivityCode[],
	) {
		this.solunoActivityCodesDropdownOptions = codes.map((code) => {
			return {
				label: this.getSolunoActivityCodeLabelDescription(code),
				value: code,
			};
		});

		this.solunoActivityCodesDropdownOptions.unshift({
			label: NotApplicableValue,
			value: {
				id: NotApplicableValue,
				description: NotApplicableValue,
				type: SolunoActivityCodeTypes.NA,
			},
		});

		this.solunoActivityCodes = codes;
	}

	@Input('ghostPracticeCodes') set _ghostPracticeCodes(
		codes: GhostPracticeCode[],
	) {
		this.ghostPracticeCodesDropdownOptions = codes.map((code) => {
			return {
				label: getGhostPracticeCodeLabelDescription(code),
				value: code,
			};
		});

		this.ghostPracticeCodesDropdownOptions.unshift({
			label: NotApplicableValue,
			value: {
				id: NotApplicableValue,
				description: NotApplicableValue,
				glAccount: '',
				taxApplies: false,
			},
		});

		this.ghostPracticeCodes = codes;
	}

	public store = inject(Store);

	public settingsSignal = toSignal(
		this.store.select(BusinessState.getDefaultSettings),
	);

	@ViewSelectSnapshot(BusinessState.getSolunoGLAccounts)
	glAccounts: SolunoGLAccount[];

	@Select(BusinessState.getLeapTaskCodes)
	leapTaskCodes$: Observable<LeapForBusiness>;

	public leapTaskCodes: LeapForBusiness;

	public solunoActivityCodes: SolunoActivityCode[] = [];

	public solunoActivityCodesDropdownOptions: SearchableDropdownItems[] = [];

	public ghostPracticeCodes: GhostPracticeCode[] = [];

	public ghostPracticeCodesDropdownOptions: SearchableDropdownItems[] = [];

	public leapFeeTaskCodesDropdownOptions: SearchableDropdownItems[] = [];
	public leapCostRecoveryTaskCodesDropdownOptions: SearchableDropdownItems[] =
		[];

	public placeholder = 'Click "Import" to import codes from LEAP';

	accountingCodeColumnLabel = computed(() => {
		// LEAP takes priority
		if (this.showLeapFeeTaskCodesDropdown()) {
			return this.statementType !== this.types.FEE
				? AccountingCodeLabels.RECOVERY_CODE
				: AccountingCodeLabels.ACTIVITY_CODE;
		}

		// GhostPractice is second priority
		if (this.showGhostPracticeCodeDropdown()) {
			return AccountingCodeLabels.GL_ACCOUNT;
		}

		// Soluno is default
		if (this.showSolunoActivityCodeDropdown()) {
			return this.statementType !== this.types.FEE
				? AccountingCodeLabels.ACTIVITY_CODE_AND_GL_ACCOUNT
				: AccountingCodeLabels.ACTIVITY_CODE;
		}

		return '';
	});

	constructor(
		private repo: BusinessStatementOfAccountsDefaultRepoService,
		private matDialog: MatDialog,
	) {}

	ngOnChanges(): void {
		if (!this.statements) {
			return;
		}
		const matchesTypeAndFile = (statement: StatementOfAccountDefault) =>
			statement.fileType === this.fileType &&
			statement.type === this.statementType;

		const showSoftwareTransactionFeeInSoa =
			this.settingsSignal()?.softwareTransactionFee?.displayIn ===
			SoftwareTransactionFeeDisplayLocation.SOA;

		const clonedStatements = structuredClone(this.statements);

		const filteredOrderedStatements: StatementOfAccountDefaultExtended[] =
			sortBy(
				clonedStatements.filter(
					(statement) =>
						matchesTypeAndFile(statement) &&
						!statement.deleted &&
						(showSoftwareTransactionFeeInSoa ||
							statement.defaultType !==
								StatementOfAccountDefaultTypes.CLOSER_FEE),
				),
				'index',
				'asc',
			);

		this.deletedStatements = clonedStatements.filter(
			(statement) => statement.deleted && matchesTypeAndFile(statement),
		);

		// Insert Intercom ID into first Conveyancing Fees statement that contains document text and will therefore have an 'Edit Text' button
		if (this.title === soaDefaultsTitles.conveyancingFees) {
			const firstConveyFeesContainsDocText = filteredOrderedStatements.find(
				(statement) => statement?.containsDocumentText,
			);

			if (firstConveyFeesContainsDocText) {
				firstConveyFeesContainsDocText.intercomId =
					'soc-conveyancing-fees-edit-text-btn';
			}
		}

		// Insert Intercom ID into first HST Disbursements statement that is calculated
		if (this.title === soaDefaultsTitles.hstDisbursements) {
			const firstHstDisbCalculatedStmt = filteredOrderedStatements.find(
				(statement) => statement.calculated,
			);

			if (firstHstDisbCalculatedStmt) {
				firstHstDisbCalculatedStmt.intercomId =
					'soc-hst-disbursements-calculated';
			}
		}

		this.filteredOrderedStatements = filteredOrderedStatements;
	}

	ngOnInit() {
		this.leapTaskCodes$
			.pipe(untilDestroyed(this))
			.subscribe((leapForBusiness) => {
				if (leapForBusiness === null) return;

				this.leapTaskCodes = leapForBusiness;

				this.leapFeeTaskCodesDropdownOptions =
					this.leapTaskCodes.feeTaskCodes?.map((code) => {
						return {
							label: getLeapCodeLabelDescription(code),
							value: code,
						};
					}) || [];

				this.leapCostRecoveryTaskCodesDropdownOptions =
					this.leapTaskCodes.costRecoveryTaskCodes?.map((code) => {
						return {
							label: getLeapCodeLabelDescription(code),
							value: code,
						};
					}) || [];

				if (
					this.leapFeeTaskCodesDropdownOptions.length > 0 ||
					this.leapCostRecoveryTaskCodesDropdownOptions.length > 0
				) {
					this.placeholder = 'Select a code';
				}
			});
	}

	setSolunoActivityCode(
		result: SolunoActivityCode,
		statement: StatementOfAccountDefault,
	) {
		this.repo.upsertStatementOfAccount(statement.id, {
			...statement,
			solunoActivityCode: result,
		});
	}

	setGhostPracticeCode(
		result: GhostPracticeCode,
		statement: StatementOfAccountDefault,
	) {
		this.repo.upsertStatementOfAccount(statement.id, {
			...statement,
			ghostPracticeCode: result,
		});
	}

	setLeapFeeTaskCode(
		result: FeeTaskCode,
		statement: StatementOfAccountDefault,
	) {
		this.repo.upsertStatementOfAccount(statement.id, {
			...statement,
			leap: { feeTaskCode: result },
		});
	}

	setLeapCostRecoveryTaskCode(
		result: CostRecoveryTaskCode,
		statement: StatementOfAccountDefault,
	) {
		this.repo.upsertStatementOfAccount(statement.id, {
			...statement,
			leap: { costRecoveryTaskCode: result },
		});
	}

	trackByFn(index: number, item: StatementOfAccountDefault): string {
		return item.id;
	}

	add() {
		const data: AddStatementOfAccountDefaultData = {
			type: this.statementType,
			index: this.filteredOrderedStatements?.length ?? 0,
			matterType: this.fileType,
		};
		this.matDialog.open(AddStatementOfAccountDefaultComponent, { data });
	}

	async createDocOrSetHide(statement: StatementOfAccountDefault) {
		if (!statement.defaultType) {
			return this.delete(statement.id);
		}
		await this.handleBusinessStatement(statement, { deleted: true });
	}

	async resetPrice(statement: StatementOfAccountDefault) {
		const globalStatement = this.remainingGlobalStatements().find(
			(s) => s.id === statement.id,
		);
		if (!globalStatement) {
			return;
		}
		await this.handleBusinessStatement(statement, {
			price: globalStatement.price,
			isBusinessDefaultPrice: false,
		});
	}

	async resetDescription(statement: StatementOfAccountDefault) {
		await this.repo.Update(statement.id, {
			description: statement?.globalDescription,
		});
	}

	async delete(id: string) {
		await this.repo.Delete(id);
	}

	async addBack(statement: StatementOfAccountDefault) {
		await this.repo.Update(statement.id, { deleted: false });
	}

	async createDocOrSetPrice(
		statement: StatementOfAccountDefault,
		price: number,
	): Promise<void> {
		if (!statement.defaultType) {
			return await this.repo.Update(statement.id, { price });
		}

		const globalStatement = this.remainingGlobalStatements().find(
			(s) => s.id === statement.id,
		);
		const isBusinessDefaultPrice = globalStatement?.price !== price;
		await this.handleBusinessStatement(statement, {
			price,
			isBusinessDefaultPrice,
		});
	}

	async createDocOrSetDescription(
		statement: StatementOfAccountDefault,
		description: string,
	): Promise<void> {
		if (!statement.defaultType) {
			return await this.repo.Update(statement.id, { description });
		}

		await this.handleBusinessStatement(statement, { description });
	}

	async _drop(event: CdkDragDrop<string[]>): Promise<void> {
		const reOrderedItems = cloneDeep(this.filteredOrderedStatements);
		moveItemInArray(reOrderedItems, event.previousIndex, event.currentIndex);

		// Determine the range of affected indexes
		const start = Math.min(event.previousIndex, event.currentIndex);
		const end = Math.max(event.previousIndex, event.currentIndex);

		// Only update items that have actually moved
		const updatePromises = reOrderedItems
			.slice(start, end + 1)
			.map(async (statement, relativeIndex) => {
				const actualIndex = start + relativeIndex;

				if (statement.index === actualIndex) {
					return;
				}

				return this.handleBusinessStatement(statement, {
					index: actualIndex,
				});
			})
			.filter((x) => !!x);

		await Promise.all(updatePromises);

		this.filteredOrderedStatements = reOrderedItems;
	}

	editDocumentText(statement: StatementOfAccountDefault) {
		const data: EditDocumentModalData = {
			statement,
		};
		this.matDialog.open(EditDocumentTextComponent, { data });
	}

	getSolunoActivityCodeLabelDescription(code: SolunoActivityCode) {
		return getSolunoActivityCodeLabelDescription(code);
	}

	getGhostPracticeCodeBackupValue(code: GhostPracticeCode) {
		return getGhostPracticeCodeLabelDescription(code);
	}

	private async handleBusinessStatement(
		statement: StatementOfAccountDefault,
		changes: Partial<StatementOfAccountDefault>,
	): Promise<void> {
		if (statement?.isBusinessDefaultPrice) {
			await this.repo.Update(statement.id, changes);
		} else {
			const data = {
				...statement,
				...changes,
			};
			await this.repo.Create(data);
		}
	}

	protected readonly StatementOfAccountType = StatementOfAccountType;
	protected readonly getLeapCodeLabelDescription = getLeapCodeLabelDescription;
}
